// lib/cameras
import {
	// CAMERA_HEIGHT,
	CAMERA_WIDTH,
	CAPTURE_FPS,
	IS_MOBILE,
} from '~/lib/constants'
import { currentMicTrack } from '~/lib/audio'
import sleep from '~/lib/sleep'
import store from '~/store'
import {
	setAvailableCameras,
	setCameraBlocked,
	setCameraLoading,
	setCameraSettings,
	setZoom,
	setZoomCapability,
} from '~/store/camera'
import { pushError } from '~/store/ui'
import { setVideoSource } from '~/lib/videoFit'
import updateVideoFitDimensions from './updateVideoFitDimensions'

const logger = console.scope('cameras')

const VIRTUAL_CAMERA_PATTERNS =
	/virtual|obs|ndi|snap\s*cam|manycam|xsplit|camtwist|streamlabs|vmix|camo|mmhmm|reincubate|chromacam|youcam|epiphan/i

export function isVirtualCamera(label) {
	if (!label) return false
	return VIRTUAL_CAMERA_PATTERNS.test(label)
}

function setCameraStream(stream) {
	window.camera_stream = stream
	if (!stream) setVideoSource()
	window.dispatchEvent(new CustomEvent('camera_stream_change'))
}

let camera_busy = false
let cameraGeneration = 0
let recoveryAttempts = 0
let lastIncludeAudio = true // Remember audio preference for recovery
const MAX_RECOVERY_ATTEMPTS = 3
const RECOVERY_RESET_DELAY = 10000 // Reset recovery counter after 10s of stability
const CAMERA_SWITCH_DELAY = 1.0 // Delay in seconds when switching cameras
const CAMERA_BUSY_TIMEOUT = 5000 // Max time to wait for camera to become available
const CAMERA_BUSY_POLL_INTERVAL = 80 // How often to check if camera is free
const CAMERA_RETRY_ATTEMPTS = 2
const CAMERA_RETRY_DELAYS = [0.2, 0.4] // Progressive delays between retries (seconds)

/**
 * Wait for camera_busy to become false, with timeout
 * @returns {Promise<boolean>} true if camera became available, false if timeout
 */
async function waitForCameraAvailable() {
	if (!camera_busy) return true

	const startTime = Date.now()
	while (camera_busy) {
		if (Date.now() - startTime > CAMERA_BUSY_TIMEOUT) {
			logger.warn('Timeout waiting for camera to become available')
			return false
		}
		await sleep(CAMERA_BUSY_POLL_INTERVAL / 1000)
	}
	return true
}

/**
 * Attempt to get camera stream with retries for transient errors
 * @param {string} deviceId - Camera device ID
 * @param {object} constraints - getUserMedia video constraints
 * @param {object|boolean|undefined} audioConstraints - Audio constraints or false to disable
 * @returns {Promise<MediaStream|null>} The stream or null if all retries failed
 */
async function getUserMediaWithRetry(deviceId, constraints, audioConstraints, isStale) {
	let lastError = null

	for (let attempt = 0; attempt < CAMERA_RETRY_ATTEMPTS; attempt++) {
		if (isStale?.()) return { error: { name: 'StaleRequest' } }
		let stream = null
		try {
			if (attempt > 0) {
				logger.info(`Camera retry attempt ${attempt + 1}/${CAMERA_RETRY_ATTEMPTS}`)
			}
			const mediaConstraints = {
				video: { deviceId: { exact: deviceId }, ...constraints },
			}
			// Add audio constraints if provided
			if (audioConstraints) {
				mediaConstraints.audio = audioConstraints
			}
			stream = await navigator.mediaDevices.getUserMedia(mediaConstraints)
			if (isStale?.()) {
				stream.getTracks().forEach(t => t.stop())
				return { error: { name: 'StaleRequest' } }
			}
			if (attempt > 0) {
				logger.info(`Camera acquired on retry ${attempt + 1}`)
			}
			return stream
		} catch (e) {
			// Clean up any partial stream that may have been acquired
			if (stream) {
				stream.getTracks().forEach(track => track.stop())
			}
			lastError = e
			logger.warn(`Camera attempt ${attempt + 1} failed:`, e.name, e.message)

			// Only retry on transient errors that might resolve with time
			const isRetryableError =
				e.name === 'NotReadableError' || // Camera in use or hardware issue
				e.name === 'AbortError' || // Operation was aborted
				e.name === 'NotFoundError' // Device temporarily unavailable

			if (!isRetryableError) {
				logger.info(`Error ${e.name} is not retryable, giving up`)
				break
			}

			// Wait before next retry (progressive delay)
			if (attempt < CAMERA_RETRY_ATTEMPTS - 1) {
				const delay = CAMERA_RETRY_DELAYS[attempt] || 1.0
				logger.info(`Waiting ${delay}s before retry...`)
				await sleep(delay)
			}
		}
	}

	return { error: lastError }
}

// TODO: convert these functions to thunks and move into the camera slice

export async function checkCameraPermission() {
	try {
		const result = await navigator.permissions.query({ name: 'camera' })
		return result.state // 'granted', 'denied', or 'prompt'
	} catch {
		return 'prompt' // fallback if permissions API unavailable
	}
}

export async function enumerateCamerasQuiet() {
	logger.info('Quiet camera enumeration (no permission prompt)...')
	try {
		const devices = await navigator.mediaDevices.enumerateDevices()
		const cameras = devices
			.filter(device => device.kind === 'videoinput' && device.deviceId && device.label)
			.map(({ deviceId, label }) => ({ deviceId, label }))
		if (cameras.length > 0) {
			logger.info('Quiet enumerate found labeled devices', cameras)
			store.dispatch(setAvailableCameras(cameras))
			return true
		}
		logger.info('Quiet enumerate: no labeled devices (permission not yet granted)')
		return false
	} catch (error) {
		logger.warn('Quiet enumerate failed', error)
		return false
	}
}

export async function getCameras() {
	logger.info('Getting cameras...')
	store.dispatch(setCameraLoading(true))
	try {
		// trigger permission request
		const stream = await navigator.mediaDevices.getUserMedia({ video: true })
		// stop stream
		stream.getTracks().forEach(track => track.stop())
	} catch (error) {
		logger.warn('Could not get camera permission, continuing without it', error)
		if (error.name === 'NotAllowedError') {
			store.dispatch(setCameraBlocked(true))
			store.dispatch(setCameraLoading(false))
			return false
		}
	}

	camera_busy = true
	try {
		const devices = await navigator.mediaDevices.enumerateDevices()
		const cameras = devices
			.filter(device => device.kind === 'videoinput')
			.filter(device => {
				try {
					return device.deviceId && typeof device.label === 'string'
				} catch (e) {
					logger.warn('Skipping problematic device', device, e)
					return false
				}
			})
			.map(({ deviceId, label }) => ({ deviceId, label }))
		logger.info('Found devices', cameras)
		store.dispatch(setAvailableCameras(cameras))
		return true
	} catch (error) {
		logger.error('Error enumerating devices', error)
		store.dispatch(setAvailableCameras([]))
		return true
	} finally {
		camera_busy = false
		store.dispatch(setCameraLoading(false))
	}
}

// Opens run one at a time, in call order; a slow getUserMedia or an open screen picker holds the next.
let openQueue = Promise.resolve()

/** Serializes acquisition against enumeration and other opens: queues behind earlier opens, waits out enumeration's `camera_busy`, holds it for `fn`. */
function whileBusy(fn) {
	const run = openQueue.then(async () => {
		if (camera_busy && !(await waitForCameraAvailable())) {
			logger.error('Enumeration still busy after timeout, forcing reset')
			camera_busy = false
		}
		camera_busy = true
		try {
			return await fn()
		} finally {
			camera_busy = false
		}
	})
	openQueue = run.catch(() => {})
	return run
}

/**
 * Opens a camera WITHOUT touching whatever is on stage: getUserMedia with retries and fallbacks, then the capture constraints. The caller owns the stream.
 * @returns {Promise<{stream: MediaStream}|{error: {name: string}}>}
 */
export const openCamera = (deviceId, { includeAudio = false, isStale } = {}) =>
	whileBusy(async () => {
		if (isStale?.()) return { error: { name: 'StaleRequest' } }
		logger.info(`Attempting to get camera stream for device: ${deviceId}`)

		// Audio processing always disabled to prevent Chrome APM mono downmix
		const audioConstraints = includeAudio
			? {
					echoCancellation: false,
					noiseSuppression: false,
					autoGainControl: false,
				}
			: false

		let result = await getUserMediaWithRetry(
			deviceId,
			{ width: 9999, aspectRatio: { ideal: 16 / 9 } },
			audioConstraints,
			isStale
		)

		// If that failed with OverconstrainedError, try with minimal constraints
		if (result?.error?.name === 'OverconstrainedError') {
			logger.info('Retrying with minimal constraints')
			result = await getUserMediaWithRetry(deviceId, {}, audioConstraints, isStale)
		}

		// If audio was requested but failed due to permission/device issues, fall back to video-only
		if (result?.error && audioConstraints) {
			const isAudioError = result.error.name === 'NotAllowedError' || result.error.name === 'NotFoundError'
			if (isAudioError) {
				logger.warn(`Audio failed (${result.error.name}), falling back to video-only`)
				result = await getUserMediaWithRetry(
					deviceId,
					{ width: 9999, aspectRatio: { ideal: 16 / 9 } },
					false,
					isStale
				)
				if (result?.error?.name === 'OverconstrainedError') {
					result = await getUserMediaWithRetry(deviceId, {}, false, isStale)
				}
			}
		}

		if (result?.error) return result

		const stream = result
		logger.info('Successfully got camera stream')
		const track = stream.getVideoTracks()[0]
		const capabilities = track.getCapabilities()
		logger.info('Capabilities', JSON.parse(JSON.stringify(capabilities)))

		// Apply all desired constraints in a single call IMPORTANT: applyConstraints replaces ALL previous constraints, so everything must be in one call
		try {
			const constraints = {
				width: { ideal: CAMERA_WIDTH, max: CAMERA_WIDTH },
				// height: { ideal: CAMERA_HEIGHT, max: CAMERA_HEIGHT },
				aspectRatio: { ideal: 16 / 9 },
				resizeMode: 'crop-and-scale',
				// zoom: 1,
			}

			// Only include constraints that are supported by the device
			const supportedConstraints = {}
			if ('width' in capabilities) supportedConstraints.width = constraints.width
			if ('aspectRatio' in capabilities) supportedConstraints.aspectRatio = constraints.aspectRatio
			if ('resizeMode' in capabilities) supportedConstraints.resizeMode = constraints.resizeMode
			// if ('zoom' in capabilities) supportedConstraints.zoom = constraints.zoom

			if ('frameRate' in capabilities) {
				const targetFps = Math.min(capabilities.frameRate.max, CAPTURE_FPS)
				logger.info(`Trying to set camera to ${targetFps}fps`)
				supportedConstraints.frameRate = targetFps
			}

			await track.applyConstraints(supportedConstraints)
		} catch (e) {
			logger.warn('Could not apply all desired camera constraints:', e)
		}
		return { stream }
	})

/**
 * Opens a screen/window/tab capture WITHOUT touching whatever is on stage. Needs a user gesture.
 * @returns {Promise<{stream: MediaStream}|{error: Error}>}
 */
export const openScreen = () =>
	whileBusy(async () => {
		try {
			return { stream: await navigator.mediaDevices.getDisplayMedia({ video: { cursor: 'hidden' } }) }
		} catch (error) {
			logger.error('openScreen', 'Error getting screen stream', error)
			return { error }
		}
	})

/** A failed camera open, surfaced: a denied permission flags `blocked`, anything else raises an error dialog. */
export function reportCameraError(deviceId, error) {
	const cameras = store.getState().camera.available || []
	const cameraLabel = cameras.find(c => c.deviceId === deviceId)?.label || 'Unknown camera'
	logger.error('Failed to get camera stream after all retries:', {
		name: error.name,
		message: error.message,
		constraint: error.constraint,
		deviceId,
		label: cameraLabel,
	})
	if (error.name === 'NotAllowedError') {
		store.dispatch(setCameraBlocked(true))
	} else {
		store.dispatch(
			pushError({
				code: 'camera_failed',
				title: 'Camera Error',
				message: `Failed to access "${cameraLabel}": ${error.message || error.name}`,
			})
		)
	}
}

function showOnCameraVideo(stream) {
	if (!window.camera_video) return
	try {
		window.camera_video.srcObject = stream
		if (stream) window.camera_video.play().catch(() => {})
	} catch (e) {
		logger.error('Error setting video source', e)
	}
}

/** The on-stage camera's track, published: zoom capability and the hardware-settings rows. */
function publishCameraTrack(track) {
	store.dispatch(setCameraBlocked(false))
	const capabilities = track.getCapabilities()
	if ('zoom' in capabilities && capabilities.zoom) {
		const { min, max, step } = capabilities.zoom
		logger.info(`Camera supports zoom: ${min}x - ${max}x (step: ${step})`)
		store.dispatch(setZoomCapability({ min, max, step }))
	} else {
		store.dispatch(setZoomCapability(null))
	}

	const trackSettings = track.getSettings()
	logger.info('Settings', JSON.parse(JSON.stringify(trackSettings)))

	// Log actual achieved frame rate for diagnostics
	const targetFps = store.getState().settings?.fps || 30
	if (trackSettings.frameRate) {
		logger.info(`📹 Camera actual FPS: ${trackSettings.frameRate} (requested: ${targetFps})`)
		if (trackSettings.frameRate < targetFps - 5) {
			logger.warn(`⚠️ Camera only providing ${trackSettings.frameRate}fps - hardware limited`)
		}
	}
	store.dispatch(setCameraSettings({ capabilities, settings: trackSettings }))
}

/** Stage bridge (sources-v2 D2): `window.camera_stream` is a wrapper over the source's video tracks, so the mic `audio.js` attaches never lands on a stream an armed source keeps. */
export function showSourceStream(stream, id) {
	const prev = window.camera_stream
	const next = stream ? new MediaStream(stream.getVideoTracks()) : null
	// The mic rides the stage, not the source; `audio.js` owns its lifetime, so a stage with nothing on it leaves it live on the sender.
	for (const t of prev?.getAudioTracks() ?? []) prev.removeTrack(t)
	const mic = currentMicTrack()
	if (next && mic) next.addTrack(mic)
	setCameraStream(next)
	showOnCameraVideo(next)
	if (!next || id === 'screen') store.dispatch(setCameraSettings())
	if (!next) return
	updateVideoFitDimensions()
	if (id !== 'screen') publishCameraTrack(next.getVideoTracks()[0])
}

/** Mobile and /camera acquisition: stops the current stream FIRST, then opens the next. Desktop goes through `src/lib/sources/manager.js`. */
export const getCamera = async (camera, includeAudio = true) => {
	const gen = ++cameraGeneration
	const isStale = () => gen !== cameraGeneration
	logger.info('getCamera', camera, { includeAudio })
	// Store for recovery
	lastIncludeAudio = includeAudio
	// turn off previous camera
	let pause = false
	if (window.camera_stream) {
		// Clear reference BEFORE stopping so onended handler knows it's intentional
		const streamToStop = window.camera_stream
		setCameraStream(null)
		showOnCameraVideo(null)
		streamToStop.getTracks().forEach(track => track.stop())
		pause = true
	}

	if (!camera?.length || camera === 'none') {
		store.dispatch(setCameraLoading(false))
		store.dispatch(setCameraSettings())
		return
	}

	if (pause && IS_MOBILE) {
		await sleep(CAMERA_SWITCH_DELAY)
		if (isStale()) return
	}

	logger.info(`Getting camera stream...`)
	store.dispatch(setCameraLoading(true))

	// Get stream with retry logic for transient failures (common on mobile camera switch)
	const { stream, error } = await openCamera(camera, { includeAudio, isStale })

	if (isStale()) {
		stream?.getTracks().forEach(t => t.stop())
		return
	}

	if (error) {
		store.dispatch(setCameraLoading(false))
		reportCameraError(camera, error)
		return
	}

	setCameraStream(stream)
	showOnCameraVideo(stream)
	updateVideoFitDimensions()
	const track = stream.getVideoTracks()[0]
	publishCameraTrack(track)
	store.dispatch(setCameraLoading(false))

	// Add track ended handler for auto-recovery
	track.onended = () => {
		// If camera_stream is null, this was an intentional stop (camera switch)
		if (!window.camera_stream) {
			logger.info('Camera track ended (intentional)')
			return
		}

		logger.warn('Camera track ended unexpectedly')

		// Prevent infinite recovery loops
		if (recoveryAttempts >= MAX_RECOVERY_ATTEMPTS) {
			logger.error(`Camera recovery failed after ${MAX_RECOVERY_ATTEMPTS} attempts`)
			recoveryAttempts = 0
			return
		}

		recoveryAttempts++
		logger.info(`Attempting camera recovery (attempt ${recoveryAttempts}/${MAX_RECOVERY_ATTEMPTS})...`)

		const currentCamera = store.getState().camera.device
		if (currentCamera) {
			// Clear the stale stream reference before re-acquiring
			setCameraStream(null)
			getCamera(currentCamera, lastIncludeAudio)
		}
	}

	// Reset recovery counter after stable operation
	setTimeout(() => {
		if (window.camera_stream === stream) {
			recoveryAttempts = 0
		}
	}, RECOVERY_RESET_DELAY)
}

/**
 * Apply hardware zoom to the camera Serializes calls to prevent concurrent applyConstraints which causes stutter
 * @param {number} value - Zoom level to apply
 * @returns {Promise<boolean>} true if zoom was applied successfully
 */
let zoomInProgress = false
let pendingZoom = null

export const applyZoom = async value => {
	// If already applying, queue this value for later
	if (zoomInProgress) {
		pendingZoom = value
		return true
	}

	const track = window.camera_stream?.getVideoTracks()[0]
	if (!track) {
		logger.warn('Cannot apply zoom: no video track available')
		return false
	}

	const zoomCapability = store.getState().camera.zoomCapability
	if (!zoomCapability) {
		logger.warn('Cannot apply zoom: camera does not support zoom')
		return false
	}

	// Clamp value to capability range
	const clampedValue = Math.max(zoomCapability.min, Math.min(zoomCapability.max, value))

	zoomInProgress = true
	try {
		await track.applyConstraints({ advanced: [{ zoom: clampedValue }] })
		store.dispatch(setZoom(clampedValue))
	} catch (e) {
		logger.error('Failed to apply zoom:', e)
	}
	zoomInProgress = false

	// Process any pending zoom value
	if (pendingZoom !== null) {
		const next = pendingZoom
		pendingZoom = null
		applyZoom(next)
	}

	return true
}
